import { XMarkIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { cn } from '@/utils/classname';

interface InfoCalloutProps {
  /** Icon rendered to the left of the content area. */
  icon: ReactNode;
  /** Main content — text, links, headings, etc. */
  children: ReactNode;
  /** When provided, a dismiss (×) button is rendered on the right. */
  onDismiss?: () => void;
  /** Accessible label for the dismiss button (required when onDismiss is set). */
  dismissLabel?: string;
  /** Optional ARIA role for the outer container (e.g. "region"). */
  role?: string;
  /** Optional aria-label for the outer container. */
  'aria-label'?: string;
  /** Optional test id. */
  'data-testid'?: string;
  /** Extra classes merged onto the outer container. */
  className?: string;
}

/**
 * An informational callout used for notices, suggestions, and
 * contextual guidance.  Supports an optional dismiss button.
 *
 * Visual style: rounded card using Chariot design tokens with
 * automatic dark-mode treatment via CSS variables.
 */
export function InfoCallout({
  icon,
  children,
  onDismiss,
  dismissLabel = 'Dismiss',
  role: roleProp,
  className,
  ...rest
}: InfoCalloutProps) {
  return (
    <div
      role={roleProp}
      aria-label={rest['aria-label']}
      data-testid={rest['data-testid']}
      className={cn(
        'border-chariot-stroke-info bg-chariot-status-info/10 flex items-start gap-3 rounded-lg border px-4 py-3',
        className
      )}
    >
      <span className="text-chariot-status-info mt-0.5 shrink-0">{icon}</span>

      <div className="text-chariot-text-primary min-w-0 flex-1 text-sm">
        {children}
      </div>

      {onDismiss && (
        <button
          type="button"
          className="text-chariot-status-info hover:bg-chariot-status-info/20 shrink-0 rounded p-1"
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <XMarkIcon className="size-4" />
        </button>
      )}
    </div>
  );
}
